import React from "react";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  timelineItemClasses,
} from "@mui/lab";
import { Handshake, PencilSimpleLine, X } from "@phosphor-icons/react";
import { formatInTimeZone } from "date-fns-tz";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import type { CollaborationNote } from "types/collaborationNote";

interface CollaborationNotesDrawerProps {
  open: boolean;
  onClose: () => void;
  notes: CollaborationNote[];
  onEditNote: (note: CollaborationNote) => void;
  isLoading?: boolean;
}

/**
 * Renders a right-anchored drawer listing all provider collaboration notes.
 * Each row reveals a pencil edit button on hover.
 *
 * @param props
 * @param props.open - Whether the drawer is open.
 * @param props.onClose - Callback fired when the drawer closes.
 * @param props.notes - The list of collaboration notes to display.
 * @param props.onEditNote - Callback fired when edit is clicked on a note.
 */
const CollaborationNotesDrawer: React.FC<CollaborationNotesDrawerProps> = ({
  open,
  onClose,
  notes,
  onEditNote,
  isLoading,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 512, maxWidth: "100%", bgcolor: "transparent", boxShadow: "none" },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: "16px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            borderRadius: 4,
            bgcolor: "background.paper",
            overflow: "hidden",
            boxShadow: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "24px",
              py: "16px",
              flexShrink: 0,
            }}
          >
            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Handshake weight="duotone" size={20} />
              <Typography variant="h2">Collaboration Notes</Typography>
            </Box>
            <IconButton
              aria-label="Close collaboration notes drawer"
              onClick={onClose}
              size="small"
            >
              <X />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", px: "24px", pt: "24px", pb: "24px" }}>
            {isLoading ? (
              <ANLoadingScreen />
            ) : notes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No collaboration notes yet.
              </Typography>
            ) : (
            <Timeline
              sx={{
                p: 0,
                m: 0,
                [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 },
              }}
            >
              {notes.map((note, index) => {
                const isLast = index === notes.length - 1;

                return (
                  <TimelineItem
                    key={note.id}
                    sx={{
                      "& .edit-btn": { visibility: "hidden" },
                      "&:hover .edit-btn": { visibility: "visible" },
                    }}
                  >
                    <TimelineSeparator>
                      <TimelineDot variant="outlined" />
                      {!isLast && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "8px",
                        pb: isLast ? 0 : "24px",
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatInTimeZone(
                            new Date(note.createdAt),
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                            "MMMM do, yyyy h:mm a zzz"
                          )}
                        </Typography>
                        <Typography variant="body2">{note.body}</Typography>
                      </Box>
                      <IconButton
                        className="edit-btn"
                        aria-label="Edit collaboration note"
                        size="small"
                        onClick={() => onEditNote(note)}
                      >
                        <PencilSimpleLine />
                      </IconButton>
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CollaborationNotesDrawer;
